import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePullRequestBasePath} from '../stores/selectors'
import {
  conversationCommentsQueryKey,
  upsertConversationItemQueryData,
} from '../page-data/loaders/use-conversation-comments'
import type {OrgBlockable, PersonalBlockable, Reactable} from '../page-data/payloads/comment-interfaces'
import type {CommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {ConversationIssueComment, Repository} from '../page-data/payloads/conversation-comments'

export type CreateTimelineCommentMutationVariables =
  | {text: string; closePullRequest?: never; reopenPullRequest?: never}
  | {text: string; closePullRequest: true; reopenPullRequest?: never}
  | {text: string; closePullRequest?: never; reopenPullRequest: true}

type MutationResponse = {
  message: string
  commentId: number
  issueComment: IssueComment
}

export function useCreatePullRequestTimelineCommentMutation() {
  const basePath = usePullRequestBasePath()
  const apiUrl = `${basePath}/page_data/${PageData.submitPullRequestTimelineComment}`

  return useMutation({
    mutationFn: async (variables: CreateTimelineCommentMutationVariables) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: variables,
      })

      const json = (await parseJSONWithBetterErrors(result)) as MutationResponse
      throwErrorsIfBadResponse(result, json)
      return {
        ...json,
        issueComment: buildConversationIssueComment(json.issueComment),
      }
    },
    onMutate: () => ({
      repositoryNwo: getCurrentRepositoryNwo(),
      tabCount: getNavigationTabCountSnapshot('pull-requests'),
      targetVersion: getNavigationTabCountTargetVersion('pull-requests'),
      updateVersion: getNavigationTabCountAuthoritativeVersion('pull-requests'),
    }),
    onSuccess: (data, inputs, {repositoryNwo, tabCount, targetVersion, updateVersion}, context) => {
      upsertConversationItemQueryData(context.client, basePath, data.issueComment)
      // buildConversationIssueComment has to fill in the blanks for unknown data, so we still need to refetch the
      // conversation to get an accurate comment. We can remove this once we migrate to a conversation-specific API
      // where the full data is available, which will allow us to avoid refetching the whole conversation.
      // FIXME: investigate returning a ConversationIssueComment from create_timeline_comment, or fetching the
      // created comment from the conversation API, before removing this adapter and the full refetch.
      context.client.invalidateQueries({queryKey: conversationCommentsQueryKey(basePath), exact: true})

      if (inputs.closePullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: -1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      } else if (inputs.reopenPullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: 1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      }
    },
  })
}

/**
 * Legacy issue comment model returned by the create endpoint.
 * @deprecated use the new conversation comments models instead */
export interface IssueComment extends OrgBlockable, PersonalBlockable, Reactable {
  id: string
  relayId: string
  authorAssociation: CommentAuthorAssociation
  authorAvatarUrl: string | null
  isAgent?: boolean
  authorLogin: string
  body: string
  bodyHtml: SafeHTMLString
  createdAt: string
  isHidden: boolean
  minimizedReason: string | null
  publishedAt: string | null
  repository: Repository | null
  url: string
}

/**
 * Temporary shim mapping the legacy issue comments payload onto the conversation comments model.
 * @deprecated use conversation comments models directly
 */
function buildConversationIssueComment(comment: IssueComment): ConversationIssueComment {
  return {
    bodyVersion: '',
    lastUserContentEdit: null,
    stafftoolsUrl: null,
    viewerCanDelete: false,
    viewerCanMinimize: false,
    viewerCanSeeMinimizeButton: false,
    viewerCanSeeUnminimizeButton: false,
    viewerCanUpdate: false,
    viewerDidAuthor: false,
    type: 'issue',
    id: Number(comment.id),
    author: {login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? '', isAgent: comment.isAgent ?? false},
    authorAssociation: comment.authorAssociation,
    body: comment.body,
    bodyHTML: comment.bodyHtml,
    createdAt: comment.createdAt,
    publishedAt: comment.publishedAt,
    isHidden: comment.isHidden,
    minimizedReason: comment.minimizedReason,
    reactionGroups: comment.reactionGroups,
    relayId: comment.relayId,
    viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
    viewerCanBlockPersonally: comment.viewerCanBlockPersonally,
    viewerCanReact: comment.viewerCanReact,
    viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
    viewerCanUnblockPersonally: comment.viewerCanUnblockPersonally,
    repository: comment.repository,
    url: comment.url,
  }
}
